import { env } from '@/lib/env';

/**
 * Module-level bus for the "the server is running a different release than this
 * bundle" notice. The typed client discovers the drift outside React (it is
 * constructed once, at module load), so the toast cannot be raised from a hook —
 * `<ReleaseNoticeBridge />` subscribes here and renders it.
 *
 * The client already reports each distinct server release once per tab. What
 * this module adds is suppression that SURVIVES a reload: the release workflow
 * rolls the backend before the frontend, so a user who accepts the offer can be
 * served the same stale bundle back and drift again immediately. Keyed on the
 * client/server PAIR, so the drift a reload actually resolves is never
 * suppressed — a matching pair is simply never consulted.
 */

const SUPPRESSION_KEY = 'immoos.release-notice.prompted';

/** A pathological number of drifts in one tab must not grow the entry forever. */
const MAX_REMEMBERED_PAIRS = 20;

type Listener = (serverRelease: string) => void;

const listeners = new Set<Listener>();

/**
 * The router starts loading at module scope, so a request can be answered — and
 * a drift discovered — before React has mounted the bridge. Without this the
 * notice would be recorded as prompted and then dropped on the floor, and the
 * suppression would keep it from ever being raised again.
 */
let undelivered: string | null = null;

export function subscribeToReleaseNotice(listener: Listener): () => void {
  listeners.add(listener);

  if (undelivered !== null) {
    const serverRelease = undelivered;

    undelivered = null;
    listener(serverRelease);
  }

  return () => {
    listeners.delete(listener);
  };
}

export function notifyReleaseMismatch(serverRelease: string): void {
  const clientRelease = env.sentry.release;

  if (clientRelease === undefined) return;

  const pair = `${clientRelease}->${serverRelease}`;
  const prompted = readPromptedPairs();

  if (prompted.includes(pair)) return;

  writePromptedPairs([...prompted, pair].slice(-MAX_REMEMBERED_PAIRS));

  if (listeners.size === 0) {
    undelivered = serverRelease;

    return;
  }

  for (const listener of listeners) listener(serverRelease);
}

function readPromptedPairs(): readonly string[] {
  try {
    const raw = window.sessionStorage.getItem(SUPPRESSION_KEY);

    if (raw === null) return [];

    const parsed: unknown = JSON.parse(raw);

    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : [];
  } catch {
    // Private mode, blocked storage, or a hand-edited entry. Degrading to "no
    // suppression" shows one extra toast; throwing would break the request that
    // discovered the drift.
    return [];
  }
}

function writePromptedPairs(pairs: readonly string[]): void {
  try {
    window.sessionStorage.setItem(SUPPRESSION_KEY, JSON.stringify(pairs));
  } catch {
    // See `readPromptedPairs`.
  }
}
